import {
  IEventCalculatorCalculateOptions, IEventCalculatorCalculateResults,
  ITPMEventCalculator
} from "../PA.TPM.Core/PA.TPM.IF.TPM.Event.Aspects";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {TPMEventBO} from "../PA.TPM.BusinessObjects/TPMEventBO";
import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {PlanningService} from "./PlanningService";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {WB_LiftRow} from "../PA.Core/PAdbSchemaRows";
import {esWB_Lift} from "../PA.Core/PAdbSchema";
import {NumberUtils} from "../PA.Core/Basic/Numbers";


const bDebug = false

export class TPMEventCalculator
  implements ITPMEventCalculator {

  private _calcMessageProvider: MessageProvider;
  private _calcProgressProvider: ProgressProvider;
  private _tpmEventBO: TPMEventBO
  private _liftTable: {[wbPrdGrpId: string]: WB_LiftRow[]}
  private _bIsLoaded: boolean

  constructor() {
    this._calcMessageProvider = new MessageProvider()
    this._calcProgressProvider = new ProgressProvider()
    this._tpmEventBO = undefined
    this._bIsLoaded = false
    this._liftTable = {}
  }

  Register(tpmEventBO: TPMEventBO): void {
    const self = this
    const bo = tpmEventBO
    // Currently, we don't listen to anything,.. just on demand calc.

    this.UnRegister()
    this._tpmEventBO = tpmEventBO

    if (bo) {
      bo.OnLoadComplete
        .then((result) => {
          return ASync.resolve()
        })
        .then((ignore) => {

          // Subscribe

          self._bIsLoaded = true
        })
        .catch((err) => {
          console.log("Error in TPM Event Calculator Register(): ", err)
          self._bIsLoaded = false
        })
    }
  }

  UnRegister(): void {
    this._tpmEventBO = undefined
    this._bIsLoaded = false

  }

  private get bIsLoaded(): boolean {
    return this._bIsLoaded
  }


  get CalcProgressProvider(): IProgressProvider {
    return this._calcProgressProvider
  }

  get CalcMessageProvider(): IMessageProvider {
    return this._calcMessageProvider
  }

  Calculate(opts: IEventCalculatorCalculateOptions): Promise<IEventCalculatorCalculateResults> {
    const self = this

    return new Promise<IEventCalculatorCalculateResults>((resolve, error) => {
      let aBO: TPMEventBO = self._tpmEventBO
      let bDone = false

      if(_.isEmpty(aBO)){
        bDone = true
        resolve({})
        return
      }

      self.CalcMessageProvider.clearMessages()
      self.CalcProgressProvider.start("Calculating Event")
      self.CalcProgressProvider.setProgressMessage('Saving', 'Validating and Saving Event')
      // 1. Save (Validate)
      let savePromise: Promise<IBusinessObject> = undefined
      if(opts.bSkipSave){
        savePromise = ASync.resolve(aBO)
      } else {
        savePromise = aBO.Save()
      }

      savePromise
        .then((ignore) => {
          if(!aBO.bIsSaved){
            self.CalcProgressProvider.setProgressMessage('Saving', 'Unable to Save Event to Calculate')
            self.CalcProgressProvider.finish()
            bDone = true
            resolve({})
            return
          }

          // 2. Run CalcPlan
          self.CalcProgressProvider.setProgressMessage('Calculating', 'Calculating Event')

          let aPlanningService = new PlanningService()
          let calcOptions = { WB_TEVT_ID: aBO.WB_TPMEvent.cif.WB_TEVT_ID,
                              bWriteEventMetrics: true,
                              bWriteActuals: true,
                              bWriteSpendMetrics: true}
          if(bDebug)
            console.log('Event Calculate Options: ', calcOptions)
          return aPlanningService.calculateEvent(calcOptions)
        })
        .then((results) => {
          if(bDone){
            return
          }

          if(results.bSuccess){
            self.CalcMessageProvider.addMessage({code: 'Success', type: MessageType.Information,
              title: 'Calculation Completed', description: 'Calculation Completed'})
          }

          // Add Any Warnings
          self._calcMessageProvider.addTextMessages('CalcW', MessageType.Warning, results.sWarnings);

          // Add Any Errors
          self._calcMessageProvider.addTextMessages('CalcE', MessageType.Error, results.sErrors)

          if(bDebug && results.sErrors.length){
            console.error("Event Calculation Results has Errors: ", results)
          }

          // 3. Reload BO
          self.CalcProgressProvider.setProgressMessage('Reloading', 'Reloading Plan')
          return aBO.Reload()
        })
        .then(() => {
          self.CalcProgressProvider.finish()
          resolve({})
        })
        .catch((err) => {
          self.CalcProgressProvider.exception(err)
          console.error("Event Calculation Results has Errors: ", err)
          error(err)
        })

    })
  }


  get bHasCalculateLift(): boolean {

    if(_.isEmpty(this._tpmEventBO)){
      return false
    }

    return (this._tpmEventBO?.WB_TPMEvent?.WB_EventTypeRow?.cif?.PGCNSLIFTPCNT_POLICY == 'CalcLiftTable')

  }


  async CalculateLift(): Promise<void> {
    //--
    const self = this
    const bo = this._tpmEventBO
    if(_.isEmpty(bo))
      return

    let arrEvtPGRows = _.filter(bo.WB_TPMEvent.WB_TPM_EventProductGroupRows, (a) => a.isActive)
    let dctPGs = _.keyBy(arrEvtPGRows, (b) => b.cif.WB_PRDGRP_ID)
    let arrKeys = _.keys(dctPGs)
    if(_.isEmpty(arrKeys))
      return

    let arrLoadKeys = _.filter(arrKeys, (a) => !(a in self._liftTable))
    if(!_.isEmpty(arrLoadKeys)){
      let arrLiftRows: WB_LiftRow[] = <WB_LiftRow[]> await bo.dbContext.findAll(esWB_Lift, {where: {WB_CUST_ID: bo.WB_TPMEvent.cif.WB_CUST_ID_PLANNING, WB_PRDGRP_ID: {inq: arrLoadKeys}, DELETED_IND: {neq: '1'}}})
      let dctGrpBy= _.groupBy(arrLiftRows, (a) => a.cif.WB_PRDGRP_ID)
      _.forEach(arrLoadKeys, (wbPrdGrpId: string) => {
        let liftRows = dctGrpBy[wbPrdGrpId] || []
        liftRows = _.sortBy(liftRows, (a)=> a.cif.LEVER_MIN)
        self._liftTable[wbPrdGrpId] = liftRows
      })
    }

    for(let aRow of arrEvtPGRows){
      let wbPrdGrpId = aRow.cif.WB_PRDGRP_ID

      let liftScaleBy = 1

      //-- Lookup Pcnt Discount Scaleup Pcnt
      let pcntDiscount = 0
      if((aRow.cif.BASE_PRICE > 0) && (aRow.cif.PROMOTED_PRICE > 0)){
        pcntDiscount = Math.min((aRow.cif.BASE_PRICE - aRow.cif.PROMOTED_PRICE)/ aRow.cif.BASE_PRICE,1)
      }
      if(pcntDiscount > 0){
        let liftRows = self._liftTable[wbPrdGrpId] || []
        let liftRow = _.first(_.filter(liftRows, (a) =>
            (a.cif.LIFT_LEVER_ID == 'PcntDiscount')
            && (a.cif.LEVER_MIN <= pcntDiscount)
            && (a.cif.LEVER_MAX >= pcntDiscount)))

        if(liftRow && liftRow.cif.LIFT_PCNT > 0){
          liftScaleBy *= (1+liftRow.cif.LIFT_PCNT)
        }
      }

      // Lookup any other Lift Predictors
      liftScaleBy = NumberUtils.round(liftScaleBy,4)
      aRow.cif.CNS_LIFT_PCNT = liftScaleBy - 1


    }





  }

}


